import { useCallback, useRef, useState, type DragEvent } from 'react';
import { dealBoardSchema, type Deal, type DealBoard } from '@forecastify/shared';
import {
  Avatar,
  DropdownMenu,
  Icon,
  ListToolbar,
  Skeleton,
  Toaster,
  Tooltip,
  cn,
  type MenuItem,
  type Toast,
} from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { DEAL_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { useFirstLoad } from '../use-first-load';
import { shortDay } from './board-format';
import { dealModeToolbar } from './board-toolbar';
import { dealMoney, dealMoneyShort, dealRefusal, initialsOf } from './deal-kit';
import type { DealModeProps } from './deals-modes';
import { dealRowMenu } from './deals-list-screen';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D4 · *Dealek — Pipeline* · `phase6/02`, `02b`.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A column per open status, the closed deals in a collapsible column at the end. `GET
 * /deals/board` is the data; dragging a card to another column is `POST /deals/:id/status`,
 * applied optimistically and reverted with the refusal's sentence when the server says no
 * (`state-rules.md` §7). The move's toast offers *undo*, which is the reverse move — a real write
 * that can fail and says so.
 *
 * ⚠️⚠️ **NOT THE KIT `Board`, and the reasons are the mockup's.** The kit's column head carries a
 * name and a count only, and this one carries the column's Σ value, its weighted value and the
 * status's criteria; the kit's card menu is a move menu only, and this one is the list's row menu.
 * Neither is a slot the kit offers. The frame here is the kit board's measured frame (`bg-muted/40`
 * columns, `rounded-lg border p-2.5 shadow-xs` cards) at the mockup's 276px.
 *
 * ⚠️ **The keyboard path is the card menu's *Move to* entries**, the same write as the drag —
 * HTML5 drag-and-drop cannot be driven from a keyboard, nor from a finger.
 */

type T = ReturnType<typeof useT>;

/** The server's order: expected close ascending, undated last, then the name. */
function byClose(a: Deal, b: Deal): number {
  if (a.closeDate !== b.closeDate) {
    if (a.closeDate === null) return 1;
    if (b.closeDate === null) return -1;
    return a.closeDate < b.closeDate ? -1 : 1;
  }
  return a.name.localeCompare(b.name);
}

/** The board with one deal moved to another open column — the optimistic write and its revert. */
function relocate(board: DealBoard, dealId: string, statusId: string): DealBoard {
  const deal = board.columns.flatMap((column) => column.deals).find((one) => one.id === dealId);
  const target = board.columns.find((column) => column.statusId === statusId);
  if (deal === undefined || target === undefined) return board;
  const moved: Deal = {
    ...deal,
    statusId,
    statusName: target.name,
    statusColor: target.color,
    daysInStage: 0,
    rotting: false,
  };
  return {
    ...board,
    columns: board.columns.map((column) => {
      const rest = column.deals.filter((one) => one.id !== dealId);
      return column.statusId === statusId
        ? { ...column, deals: [...rest, moved].sort(byClose) }
        : { ...column, deals: rest };
    }),
  };
}

export function DealBoardScreen(props: DealModeProps) {
  const { feeds, canManage, actions } = props;
  const t = useT();
  const { locale } = useI18n();
  const session = useSession();
  const [board, setBoard] = useState<DealBoard | null>(null);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [search, setSearch] = useState('');
  const [owner, setOwner] = useState('');
  const [closedOpen, setClosedOpen] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  /** ⚠️ A search is a request per keystroke; only the latest answer may land. */
  const ticket = useRef(0);

  const toast = useCallback((entry: Omit<Toast, 'id'>) => {
    setToasts((current) => [...current, { ...entry, id: `${String(Date.now())}-${entry.title}` }]);
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const mine = ++ticket.current;
    const params = new URLSearchParams();
    if (owner !== '') params.set('owner', owner);
    if (search.trim() !== '') params.set('search', search.trim());
    const text = params.toString();
    const response = await session
      .request(`/deals/board${text === '' ? '' : `?${text}`}`)
      .catch(() => null);
    if (mine !== ticket.current) return;
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const parsed = dealBoardSchema.safeParse(await response.json());
    if (mine !== ticket.current) return;
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setBoard(parsed.data);
  }, [session, owner, search]);

  const loading = useFirstLoad(reload);
  useLiveInvalidation(DEAL_TOPICS, reload);

  const postStatus = useCallback(
    (deal: Deal, statusId: string) =>
      session
        .request(`/deals/${deal.id}/status`, {
          method: 'POST',
          body: JSON.stringify({ statusId }),
        })
        .catch(() => null),
    [session],
  );

  const move = (deal: Deal, statusId: string): void => {
    if (board === null || deal.statusId === statusId) return;
    const from = deal.statusId;
    const target = board.columns.find((column) => column.statusId === statusId);
    if (target === undefined) return;
    setBoard((current) => (current === null ? current : relocate(current, deal.id, statusId)));
    void (async () => {
      const response = await postStatus(deal, statusId);
      if (!response?.ok) {
        setBoard((current) => (current === null ? current : relocate(current, deal.id, from)));
        toast({
          title: response === null ? t('error.unavailable') : await dealRefusal(response, t),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('dealBoard.toast.moved', { name: target.name }),
        description: deal.name,
        variant: 'success',
        action: {
          label: t('dealBoard.toast.undo'),
          failureLabel: t('dealBoard.toast.undoFailed'),
          onAction: async () => {
            const back = await postStatus(deal, from);
            if (!back?.ok) throw new Error('undo refused');
            await reload();
          },
        },
      });
      /* The move set the deal's probability to the status's suggested one (`setStatus`), so the
         card's chip and the columns' weighted sums are re-read rather than guessed. */
      await reload();
    })();
  };

  const findDeal = (id: string): Deal | undefined =>
    board?.columns.flatMap((column) => column.deals).find((deal) => deal.id === id);

  const probabilityColor = (pct: number): string =>
    feeds.config?.probabilities.find((entry) => entry.pct === pct)?.color ?? 'var(--status-idle)';

  const cardMenu = (deal: Deal): MenuItem[] => {
    const items = dealRowMenu(t, deal, canManage, actions);
    const elsewhere =
      canManage && deal.statusCategory === 'open' && board !== null
        ? board.columns.filter((column) => column.statusId !== deal.statusId)
        : [];
    return elsewhere.length === 0
      ? items
      : [
          ...items,
          { type: 'separator' },
          ...elsewhere.map((column) => ({
            label: t('dealBoard.moveTo', { name: column.name }),
            dot: column.color,
            onSelect: () => {
              move(deal, column.statusId);
            },
          })),
        ];
  };

  const toolbar = {
    ...dealModeToolbar(t, props),
    search,
    onSearchChange: setSearch,
    filters: [
      {
        key: 'owner',
        label: t('deals.filter.owner'),
        value: owner,
        onChange: setOwner,
        options: feeds.members.map((member) => ({ value: member.id, label: member.displayName })),
      },
    ],
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ListToolbar {...toolbar} />
      <ScreenState failure={failure} onRetry={() => void reload()}>
        {loading || board === null ? (
          <BoardSkeleton />
        ) : board.columns.length === 0 ? (
          <p className="text-muted-foreground mx-auto max-w-[420px] px-6 py-16 text-center text-[12.5px]">
            {t('dealBoard.noStatuses')}
          </p>
        ) : (
          <div
            role="group"
            aria-label={t('deals.mode.kanban')}
            className="min-h-0 flex-1 overflow-auto p-4"
          >
            <div className="flex h-full min-w-max gap-3">
              {board.columns.map((column) => {
                const value = column.deals.reduce((sum, deal) => sum + Number(deal.value), 0);
                const weighted = column.deals.reduce(
                  (sum, deal) => sum + Number(deal.weightedValue),
                  0,
                );
                return (
                  <section
                    key={column.statusId}
                    aria-label={t('dealBoard.column', { name: column.name })}
                    className={cn(
                      'bg-muted/40 flex h-full w-[276px] shrink-0 flex-col rounded-xl border',
                      over === column.statusId && 'border-primary ring-ring/25 ring-[3px]',
                    )}
                    onDragOver={(event) => {
                      if (dragging === null) return;
                      /* `preventDefault` is what makes a drop legal at all. */
                      event.preventDefault();
                      if (over !== column.statusId) setOver(column.statusId);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      const carried = event.dataTransfer.getData('text/plain');
                      const deal = findDeal(carried === '' ? (dragging ?? '') : carried);
                      setDragging(null);
                      setOver(null);
                      if (deal !== undefined) move(deal, column.statusId);
                    }}
                  >
                    {/* The column's sums are its cards' — exactly what the server sums
                        (`deal.service.ts` `board`), so an optimistic move updates them too. */}
                    <Tooltip content={column.criteria === '' ? undefined : column.criteria} asChild>
                      <header className="flex flex-col gap-1 px-3 py-2.5">
                        <span className="flex items-center gap-2">
                          <span
                            aria-hidden
                            className="size-2 rounded-full"
                            style={{ background: column.color }}
                          />
                          <span className="text-[12.5px] font-medium">{column.name}</span>
                          <span className="text-muted-foreground text-[11.5px] tabular-nums">
                            {column.deals.length}
                          </span>
                        </span>
                        <span className="text-muted-foreground flex items-center gap-2 text-[11px] tabular-nums">
                          <span>{dealMoneyShort(value, board.currencyCode)}</span>
                          <span className="opacity-60">
                            {t('dealBoard.column.weighted', {
                              value: dealMoneyShort(weighted, board.currencyCode),
                            })}
                          </span>
                        </span>
                      </header>
                    </Tooltip>
                    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2">
                      {column.deals.map((deal) => (
                        <DealCard
                          key={deal.id}
                          t={t}
                          locale={locale}
                          deal={deal}
                          probabilityColor={probabilityColor(deal.probability)}
                          draggable={canManage}
                          dragging={dragging === deal.id}
                          menu={cardMenu(deal)}
                          onOpen={() => {
                            actions.onOpen(deal);
                          }}
                          onDragStart={(event) => {
                            event.dataTransfer.effectAllowed = 'move';
                            event.dataTransfer.setData('text/plain', deal.id);
                            setDragging(deal.id);
                          }}
                          onDragEnd={() => {
                            setDragging(null);
                            setOver(null);
                          }}
                        />
                      ))}
                      {column.deals.length === 0 ? (
                        <p className="text-muted-foreground rounded-lg border border-dashed px-3 py-5 text-center text-[11.5px] leading-relaxed">
                          {canManage ? t('dealBoard.empty') : t('dealBoard.emptyReadOnly')}
                        </p>
                      ) : null}
                    </div>
                  </section>
                );
              })}

              <ClosedColumn
                t={t}
                deals={board.closed}
                open={closedOpen}
                onToggle={() => {
                  setClosedOpen((current) => !current);
                }}
                menu={(deal) => dealRowMenu(t, deal, canManage, actions)}
                onOpen={actions.onOpen}
              />
            </div>
          </div>
        )}
      </ScreenState>
      <Toaster
        toasts={[...toasts]}
        label={t('deals.mode.kanban')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
      />
    </div>
  );
}

function DealCard({
  t,
  locale,
  deal,
  probabilityColor,
  draggable,
  dragging,
  menu,
  onOpen,
  onDragStart,
  onDragEnd,
}: {
  readonly t: T;
  readonly locale: string;
  readonly deal: Deal;
  readonly probabilityColor: string;
  readonly draggable: boolean;
  readonly dragging: boolean;
  readonly menu: MenuItem[];
  readonly onOpen: () => void;
  readonly onDragStart: (event: DragEvent) => void;
  readonly onDragEnd: () => void;
}) {
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cn(
        'bg-card hover:border-primary/40 relative flex flex-col rounded-lg border p-2.5 shadow-xs transition-colors select-none',
        draggable && 'cursor-grab',
        dragging && 'shadow-modal scale-[1.02] rotate-[1.5deg] opacity-80',
      )}
    >
      {/* ⚠️ A real `<button>` over the content: one Tab stop and one Enter opens the deal. The
          draggable wrapper cannot be it — a draggable `<button>` swallows the drag in Safari, and
          the menu inside it would be a button in a button. */}
      <button type="button" className="flex flex-col gap-1.5 text-left" onClick={onOpen}>
        <span className="flex items-center gap-2 pr-6">
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-[3px]"
            style={{ background: deal.clientColor ?? 'var(--muted-foreground)' }}
          />
          <span className="text-muted-foreground min-w-0 flex-1 truncate text-[11px]">
            {deal.clientName}
          </span>
          <Avatar
            fallback={initialsOf(deal.ownerName)}
            seed={deal.ownerMemberId}
            title={deal.ownerName}
            size={20}
          />
        </span>
        <span className="text-[12.5px] leading-snug font-medium">{deal.name}</span>
        <span className="flex items-center gap-2">
          <span className="text-[11.5px] font-medium tabular-nums">
            {dealMoney(deal.value, deal.currencyCode)}
          </span>
          {/* The hue as a wash under foreground text: four of the vocabulary's hues fail AA as
              text, and the number has to be read. */}
          <span
            className="rounded px-1.5 py-0.5 text-[10.5px] tabular-nums"
            style={{
              background: `color-mix(in oklch, ${probabilityColor} 16%, transparent)`,
            }}
          >
            {`${String(deal.probability)}%`}
          </span>
          <span className="flex-1" />
          <Tooltip
            asChild
            content={
              deal.valueMode === 'plan'
                ? t('dealBoard.card.planHint')
                : t('dealBoard.card.indicativeHint')
            }
          >
            <span className="text-muted-foreground rounded border px-1 py-0.5 text-[10px]">
              {deal.valueMode === 'plan'
                ? t('dealBoard.card.plan')
                : t('dealBoard.card.indicative')}
            </span>
          </Tooltip>
        </span>
        <span className="text-muted-foreground flex items-center gap-1.5 text-[11px] tabular-nums">
          <span>
            {deal.closeDate === null
              ? t('dealBoard.card.noClose')
              : shortDay(deal.closeDate, locale)}
          </span>
          <span aria-hidden className="opacity-60">
            ·
          </span>
          <span>{t('dealBoard.card.days', { count: deal.daysInStage })}</span>
          {deal.rotting ? (
            <Tooltip asChild content={t('deals.rotting', { count: deal.daysInStage })}>
              <span
                role="img"
                aria-label={t('deals.rotting', { count: deal.daysInStage })}
                className="bg-destructive size-1.5 rounded-full"
              />
            </Tooltip>
          ) : null}
        </span>
        <Tooltip content={deal.nextStepLate ? t('deals.next.late') : undefined} asChild>
          <span
            className={cn(
              'truncate text-[11px]',
              deal.nextStepLate ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {deal.nextStep === null ? t('dealBoard.card.noNext') : deal.nextStep.text}
          </span>
        </Tooltip>
      </button>
      <CardMenu t={t} items={menu} />
    </div>
  );
}

function CardMenu({ t, items }: { readonly t: T; readonly items: MenuItem[] }) {
  return (
    <div className="absolute top-1.5 right-1.5">
      <DropdownMenu label={t('list.rowMenu')} items={items}>
        {/* 32px for a finger, where this menu is the only way to move a card; 20px with a mouse. */}
        <button
          type="button"
          aria-label={t('list.rowMenu')}
          className="text-muted-foreground hover:text-foreground desktop:size-5 grid size-8 place-items-center rounded"
        >
          <Icon name="ellipsis" size={13} />
        </button>
      </DropdownMenu>
    </div>
  );
}

/** *Lezárt* — the won and lost deals, folded by default (`02b`). */
function ClosedColumn({
  t,
  deals,
  open,
  onToggle,
  menu,
  onOpen,
}: {
  readonly t: T;
  readonly deals: readonly Deal[];
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly menu: (deal: Deal) => MenuItem[];
  readonly onOpen: (deal: Deal) => void;
}) {
  return (
    <div className="flex w-[240px] shrink-0 flex-col">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="hover:bg-accent/40 flex items-center gap-2 rounded-lg px-2 py-2 text-left"
      >
        <Icon
          name={open ? 'chevron-down' : 'chevron-right'}
          size={13}
          className="text-muted-foreground"
        />
        <span className="text-[12.5px] font-medium">{t('dealBoard.closed')}</span>
        <span className="text-muted-foreground text-[11.5px] tabular-nums">{deals.length}</span>
      </button>
      {open ? (
        <div className="flex min-h-0 flex-col gap-1.5 overflow-y-auto px-1 pb-2">
          {deals.length === 0 ? (
            <p className="text-muted-foreground px-1 py-2 text-[11.5px]">
              {t('dealBoard.closedEmpty')}
            </p>
          ) : (
            deals.map((deal) => (
              <div
                key={deal.id}
                className="bg-card hover:bg-accent/40 relative rounded-lg border transition-colors"
              >
                <button
                  type="button"
                  className="flex w-full flex-col gap-1 p-2 pr-8 text-left"
                  onClick={() => {
                    onOpen(deal);
                  }}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-muted-foreground text-[11px] tabular-nums">
                      {deal.identifier}
                    </span>
                    <span className="text-muted-foreground flex items-center gap-1 text-[10.5px]">
                      <span
                        aria-hidden
                        className="size-1.5 rounded-full"
                        style={{ background: deal.statusColor }}
                      />
                      {deal.statusName}
                    </span>
                  </span>
                  <span className="truncate text-[12px] font-medium">{deal.name}</span>
                  <span className="text-muted-foreground text-[11px] tabular-nums">
                    {dealMoney(deal.value, deal.currencyCode)}
                  </span>
                </button>
                <CardMenu t={t} items={menu(deal)} />
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Before the first answer: the columns' frame with placeholder cards and no counts. */
function BoardSkeleton() {
  const t = useT();
  return (
    <div className="min-h-0 flex-1 overflow-auto p-4">
      <div role="status" aria-live="polite" className="sr-only">
        {t('state.loading')}
      </div>
      <div aria-hidden className="flex h-full min-w-max gap-3">
        {[3, 2, 2, 1].map((cards, at) => (
          <section
            key={at}
            className="bg-muted/40 flex h-full w-[276px] shrink-0 flex-col gap-2 rounded-xl border px-2 pt-3 pb-2"
          >
            <div className="px-1 pb-1">
              <Skeleton width="50%" />
            </div>
            {Array.from({ length: cards }, (_, index) => (
              <div key={index} className="bg-card flex flex-col gap-2 rounded-lg border p-2.5">
                <Skeleton width="40%" />
                <Skeleton width="80%" />
                <Skeleton width="55%" />
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
